import { Link } from "react-router-dom";
import type { Post } from "@/types/content";
import { categories } from "@/mocks/categories";

interface LeadStoriesProps {
  posts: Post[];
}

function catName(slug: string) {
  return categories.find((c) => c.slug === slug)?.name ?? "综合";
}

export default function LeadStories({ posts }: LeadStoriesProps) {
  const lead = posts.find((p) => p.lead) ?? posts[0];
  const secondary = posts.filter((p) => p.id !== lead.id && p.hot).slice(0, 3);

  if (!lead) return null;

  return (
    <section aria-labelledby="lead-title" className="w-full px-4 md:px-6 pb-10 md:pb-14">
      <div className="flex items-center gap-3 mb-6">
        <h2 id="lead-title" className="text-2xl md:text-3xl font-bold text-foreground-950 whitespace-nowrap">
          爆料头条
        </h2>
        <span className="flex-1 h-px rule-dashed" />
        <span className="text-xs text-foreground-500 whitespace-nowrap">编辑部当日重点整理</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
        <article className="lg:col-span-7 group relative rounded-lg overflow-hidden bg-foreground-950">
          <Link to={`/post/${lead.slug}`} className="block">
            <div className="w-full h-64 md:h-[420px]">
              <img
                src={lead.cover}
                alt={lead.alt}
                title={`${lead.title} - 网曝吃瓜爆料`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                <i className="ri-fire-fill" aria-hidden="true" />
                {catName(lead.categorySlug)}
              </span>
              <h3 className="mt-3 text-xl md:text-3xl font-bold text-background-50 leading-snug group-hover:text-accent-300 transition-colors">
                {lead.title}
              </h3>
              <p className="mt-3 text-sm text-background-200 leading-relaxed line-clamp-2 max-w-2xl">{lead.excerpt}</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-background-300">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line" aria-hidden="true" />
                  {lead.author}
                </span>
                <span>{lead.date}</span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line" aria-hidden="true" />
                  {lead.readTime} 分钟阅读
                </span>
              </div>
            </div>
          </Link>
        </article>

        <div className="lg:col-span-5 flex flex-col gap-5 md:gap-6">
          {secondary.map((post) => (
            <article key={post.id} className="group flex gap-4">
              <Link to={`/post/${post.slug}`} className="shrink-0 w-28 h-[84px] md:w-36 md:h-[104px] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={post.cover}
                  alt={post.alt}
                  title={`${post.title} - 网曝吃瓜爆料`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </Link>
              <div className="min-w-0 flex-1">
                <span className="text-xs text-secondary-700">{catName(post.categorySlug)}</span>
                <Link to={`/post/${post.slug}`} className="block mt-1">
                  <h3 className="text-sm md:text-base font-bold leading-snug text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {post.title}
                  </h3>
                </Link>
                <p className="mt-1.5 text-xs text-foreground-500 flex items-center gap-2">
                  <span>{post.date}</span>
                  <span className="text-background-400">·</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-fire-line text-primary-400" aria-hidden="true" />
                    {post.heat}
                  </span>
                </p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}